import type {
  TranscriptDisplay,
  TranscriptSummary,
  AIAnalysis,
  ScorecardRecommendation,
} from "../types";
import type {
  RawTranscript,
  RawTranscriptSummary,
  RawAIAnalysis,
  RawTopicSummary,
  RawAIQuestionScore,
} from "./rawTypes";

function toTranscriptSummary(
  raw: RawTranscriptSummary | undefined,
): TranscriptSummary | undefined {
  if (!raw || !raw.summary) return undefined;
  return {
    summary: raw.summary || "",
    topicSummaries: (raw.topicSummaries || []).map((ts: RawTopicSummary) => ({
      topic: ts.topic || "",
      detail: ts.detail || "",
    })),
  };
}

function toAIAnalysis(raw: RawAIAnalysis | undefined): AIAnalysis | undefined {
  if (!raw) return undefined;
  return {
    summary: raw.summary || "",
    keyStrengths: raw.keyStrengths || [],
    areasOfConcern: raw.areasOfConcern || [],
    questionScores: (raw.questionScores || []).map((qs: RawAIQuestionScore) => {
      const notAsked = qs.notAsked ?? false;
      return {
        questionId: qs.questionId || "",
        // Not-asked questions carry no score; null drives the "Not Asked" pill.
        score: notAsked ? null : (qs.score ?? null),
        reasoning: qs.reasoning || "",
        questionText: qs.questionText || undefined,
        notAsked,
      };
    }),
    // Surface the server-provided recommendation verbatim; do not invent a
    // default — callers must handle a missing/empty recommendation explicitly.
    recommendation: (raw.recommendation || "") as ScorecardRecommendation,
    overallComment: raw.overallComment || "",
    weightedScore: raw.weightedScore ?? 0,
  };
}

export function toTranscriptDisplay(raw: RawTranscript): TranscriptDisplay {
  const uploadedByName =
    [raw.uploadedBy?.firstName, raw.uploadedBy?.lastName]
      .filter(Boolean)
      .join(" ") || "Unknown";
  return {
    id: raw.id,
    interviewId: raw.interviewId,
    uploadedBy: {
      id: raw.uploadedBy?.id || "",
      name: uploadedByName,
    },
    text: raw.text || "",
    textHtml: raw.textHtml || undefined,
    fileName: raw.fileName,
    summary: toTranscriptSummary(raw.summary),
    aiAnalysis: toAIAnalysis(raw.aiAnalysis),
    aiAnalysisStatus:
      (raw.aiAnalysisStatus as TranscriptDisplay["aiAnalysisStatus"]) ??
      undefined,
    summaryStatus:
      (raw.summaryStatus as TranscriptDisplay["summaryStatus"]) ?? undefined,
    createdAt: raw.createdAt || "",
  };
}
